import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    IBonusRegister,
    IHourBreachReport,
    IInsuranceRegister,
    ILeaveRegister,
    IOvertimeRegister,
    IServiceRecord,
    IWageComplianceRegister,
    IWageSheetDTO,
    WageSheetFormat,
    IWppfProvision,
    IWppfRegister,
} from '../interfaces/statutoryRegister.interface';

const headers = () => AuthService.getAuthHeader();

export const StatutoryRegisterService = {
    getBonusRegister: async (institutionId: string, year: number): Promise<IBonusRegister> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.BONUS(institutionId), {
            headers: headers(),
            params: { year },
        });
        return res.data;
    },
    downloadBonusRegisterXlsx: async (institutionId: string, year: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.BONUS_XLSX(institutionId), {
            headers: headers(),
            params: { year },
            responseType: 'blob',
        });
        return res.data;
    },
    downloadBonusRegisterPdf: async (institutionId: string, year: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.BONUS_PDF(institutionId), {
            headers: headers(),
            params: { year },
            responseType: 'blob',
        });
        return res.data;
    },

    getOvertimeRegister: async (institutionId: string, year: number, month?: number): Promise<IOvertimeRegister> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.OVERTIME(institutionId), {
            headers: headers(),
            params: { year, month },
        });
        return res.data;
    },
    downloadOvertimeRegisterXlsx: async (institutionId: string, year: number, month?: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.OVERTIME_XLSX(institutionId), {
            headers: headers(),
            params: { year, month },
            responseType: 'blob',
        });
        return res.data;
    },
    downloadOvertimeRegisterPdf: async (institutionId: string, year: number, month?: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.OVERTIME_PDF(institutionId), {
            headers: headers(),
            params: { year, month },
            responseType: 'blob',
        });
        return res.data;
    },

    // T8: `year` is optional -- omitted defers to the backend's current-fiscal-year default.
    getLeaveRegister: async (institutionId: string, year?: number): Promise<ILeaveRegister> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.LEAVE(institutionId), {
            headers: headers(),
            params: year !== undefined ? { year } : undefined,
        });
        return res.data;
    },
    downloadLeaveRegisterXlsx: async (institutionId: string, year?: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.LEAVE_XLSX(institutionId), {
            headers: headers(),
            params: year !== undefined ? { year } : undefined,
            responseType: 'blob',
        });
        return res.data;
    },
    downloadLeaveRegisterPdf: async (institutionId: string): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.LEAVE_PDF(institutionId), {
            headers: headers(),
            responseType: 'blob',
        });
        return res.data;
    },

    getHourBreachReport: async (institutionId: string, year: number, month?: number): Promise<IHourBreachReport> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.HOUR_BREACHES(institutionId), {
            headers: headers(),
            params: { year, month },
        });
        return res.data;
    },
    downloadHourBreachReportXlsx: async (institutionId: string, year: number, month?: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.HOUR_BREACHES_XLSX(institutionId), {
            headers: headers(),
            params: { year, month },
            responseType: 'blob',
        });
        return res.data;
    },
    downloadHourBreachReportPdf: async (institutionId: string, year: number, month?: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.HOUR_BREACHES_PDF(institutionId), {
            headers: headers(),
            params: { year, month },
            responseType: 'blob',
        });
        return res.data;
    },

    getWppfRegister: async (institutionId: string, year: number): Promise<IWppfRegister> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WPPF(institutionId), {
            headers: headers(),
            params: { year },
        });
        return res.data;
    },
    downloadWppfRegisterXlsx: async (institutionId: string, year: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WPPF_XLSX(institutionId), {
            headers: headers(),
            params: { year },
            responseType: 'blob',
        });
        return res.data;
    },
    downloadWppfRegisterPdf: async (institutionId: string, year: number): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WPPF_PDF(institutionId), {
            headers: headers(),
            params: { year },
            responseType: 'blob',
        });
        return res.data;
    },

    // Task 6 (2026-09-11 closeout-payroll-money): WPPF §234 provision booking/reversal/retry.
    bookWppfProvision: async (
        institutionId: string, fiscalYear: number, expenseAccountId: string, payableAccountId: string, postingDate?: string,
    ): Promise<IWppfProvision> => {
        const res = await axios.post(ENDPOINTS.STATUTORY_REGISTER.WPPF_PROVISION(institutionId),
            { fiscalYear, expenseAccountId, payableAccountId, postingDate },
            { headers: headers() });
        return res.data;
    },
    reverseWppfProvision: async (institutionId: string, provisionId: string, reason?: string): Promise<IWppfProvision> => {
        const res = await axios.post(ENDPOINTS.STATUTORY_REGISTER.WPPF_PROVISION_REVERSE(institutionId, provisionId),
            { reason },
            { headers: headers() });
        return res.data;
    },
    retryWppfProvisionPosting: async (institutionId: string, provisionId: string): Promise<IWppfProvision> => {
        const res = await axios.post(ENDPOINTS.STATUTORY_REGISTER.WPPF_PROVISION_RETRY(institutionId, provisionId),
            {},
            { headers: headers() });
        return res.data;
    },
    getWageComplianceRegister: async (institutionId: string): Promise<IWageComplianceRegister> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WAGE_COMPLIANCE(institutionId), {
            headers: headers(),
        });
        return res.data;
    },
    downloadWageComplianceRegisterXlsx: async (institutionId: string): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WAGE_COMPLIANCE_XLSX(institutionId), {
            headers: headers(),
            responseType: 'blob',
        });
        return res.data;
    },
    downloadWageComplianceRegisterPdf: async (institutionId: string): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WAGE_COMPLIANCE_PDF(institutionId), {
            headers: headers(),
            responseType: 'blob',
        });
        return res.data;
    },
    getInsuranceRegister: async (institutionId: string): Promise<IInsuranceRegister> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.INSURANCE(institutionId), { headers: headers() });
        return res.data;
    },
    downloadInsuranceRegisterXlsx: async (institutionId: string): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.INSURANCE_XLSX(institutionId), {
            headers: headers(),
            responseType: 'blob',
        });
        return res.data;
    },
    downloadInsuranceRegisterPdf: async (institutionId: string): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.INSURANCE_PDF(institutionId), {
            headers: headers(),
            responseType: 'blob',
        });
        return res.data;
    },

    getWageSheet: async (
        institutionId: string, year: number, month: number, format?: WageSheetFormat, orgUnitId?: string,
    ): Promise<IWageSheetDTO> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WAGE_SHEET(institutionId), {
            headers: headers(),
            params: { year, month, format, orgUnitId },
        });
        return res.data;
    },
    downloadWageSheetXlsx: async (
        institutionId: string, year: number, month: number, format?: WageSheetFormat, orgUnitId?: string,
    ): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WAGE_SHEET_XLSX(institutionId), {
            headers: headers(),
            params: { year, month, format, orgUnitId },
            responseType: 'blob',
        });
        return res.data;
    },
    downloadWageSheetPdf: async (
        institutionId: string, year: number, month: number, format?: WageSheetFormat, orgUnitId?: string,
    ): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.WAGE_SHEET_PDF(institutionId), {
            headers: headers(),
            params: { year, month, format, orgUnitId },
            responseType: 'blob',
        });
        return res.data;
    },
    downloadAuditPack: async (
        institutionId: string, year: number, month: number, format?: WageSheetFormat,
    ): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.AUDIT_PACK(institutionId), {
            headers: headers(),
            params: { year, month, format },
            responseType: 'blob',
        });
        return res.data;
    },

    getServiceRecord: async (institutionId: string, userId: string): Promise<IServiceRecord> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.SERVICE_RECORD(institutionId), {
            headers: headers(),
            params: { userId },
        });
        return res.data;
    },
    downloadServiceRecordXlsx: async (institutionId: string, userId: string): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.SERVICE_RECORD_XLSX(institutionId), {
            headers: headers(),
            params: { userId },
            responseType: 'blob',
        });
        return res.data;
    },
    downloadServiceRecordPdf: async (institutionId: string, userId: string): Promise<Blob> => {
        const res = await axios.get(ENDPOINTS.STATUTORY_REGISTER.SERVICE_RECORD_PDF(institutionId), {
            headers: headers(),
            params: { userId },
            responseType: 'blob',
        });
        return res.data;
    },
};
